"use client";

import { useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { SettingsCard } from "../SettingsCard";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import {
  getCurrencySymbol,
  getPriceTiers,
  PriceCurrency,
  type PricingConfiguration,
} from "@/features/calendar/types/pricing.tsx";
import { updateDefaultPricing } from "@/features/calendar/api/Pricing";

const defaultPricingSchema = (t: any) =>
  z.object({
    amount: z.number().min(0.01, t("forms:templates.validation.priceMinimium")).max(9999),
    currency: z.nativeEnum(PriceCurrency),
  });

// Define the form values type
type DefaultPricingFormValues = z.infer<ReturnType<typeof defaultPricingSchema>>;

interface DefaultPricingCardProps {
  isLoading?: boolean;
  defaultPricing: PricingConfiguration | null;
  onPricingUpdated: () => void;
}

export const DefaultPricingCard = ({
  isLoading = false,
  defaultPricing,
  onPricingUpdated,
}: DefaultPricingCardProps) => {
  const { t } = useTranslation(["common", "calendar", "forms"]);
  const form = useForm<DefaultPricingFormValues>({
    resolver: zodResolver(defaultPricingSchema(t)),
    defaultValues: {
      amount: defaultPricing?.amount || undefined,
      currency: (defaultPricing?.currency as PriceCurrency) || PriceCurrency.Usd,
    },
    mode: "onChange",
  });

  // Watch form values for changes
  const currency = form.watch("currency");

  // Initialize form with default pricing data
  useEffect(() => {
    if (defaultPricing) {
      form.reset({
        amount: defaultPricing.amount,
        currency: defaultPricing.currency as PriceCurrency,
      });
    }
  }, [defaultPricing, form]);

  // Form submission handler
  const onSubmit = async (values: DefaultPricingFormValues) => {
    try {
      await updateDefaultPricing({
        amount: values.amount,
        currency: values.currency,
      });

      toast.success(t("calendar:pricing.defaultPricingUpdated"));
      onPricingUpdated();
      form.reset(values);
    } catch (error: any) {
      console.error("Failed to update default pricing:", error);

      // Handle specific error responses
      if (error.response?.data?.errors.amount[0]) {
        const minPrice = error.response?.data?.min_price || 10;

        form.setError("amount", {
          type: "manual",
          message: t("calendar:pricing.minimumPriceError", {
            symbol: getCurrencySymbol(PriceCurrency.Usd),
            price: minPrice,
          }),
        });
      } else if (error.response?.status === 422) {
        toast.error(t("common:errors.invalidPricingData"), {
          description: t("common:errors.checkPriceAndCurrency"),
        });
      } else if (error.response?.status === 403) {
        toast.error(t("common:errors.permissionDenied"), {
          description: t("common:errors.noPermissionToUpdatePricing"),
        });
      } else {
        toast.error(t("calendar:pricing.failedToUpdateDefaultPricing"), {
          description: t("common:errors.tryAgainOrContactSupport"),
        });
      }
    }
  };

  // Handle price tier selection
  const handlePriceTierClick = (price: number) => {
    form.setValue("amount", price, { shouldDirty: true, shouldValidate: true });
    form.clearErrors("amount");
  };

  return (
    <SettingsCard
      action={
        form.formState.isDirty && (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.15 }}
          >
            <Button
              className={"flex items-center gap-1 max-[400px]:w-full sm:w-auto"}
              disabled={form.formState.isSubmitting || !form.formState.isValid}
              size="sm"
              variant={"foreground"}
              onClick={form.handleSubmit(onSubmit)}
            >
              {form.formState.isSubmitting ? (
                <>
                  <Icon
                    className="animate-spin"
                    height={"1rem"}
                    icon="mdi:loading"
                    width={"1rem"}
                  />
                  {t("common:saving")}
                </>
              ) : (
                <>
                  <Icon height={"1rem"} icon="mdi:content-save-outline" width={"1rem"} />
                  {t("common:save")}
                </>
              )}
            </Button>
          </motion.div>
        )
      }
      description={t("calendar:pricing.defaultPricingDescription")}
      icon="mdi:cash-multiple"
      title={t("calendar:pricing.defaultPricing")}
    >
      <Form {...form}>
        <div className="space-y-4">
          <div className="flex flex-col space-y-1.5">
            <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
              <label
                className="text-accent-foreground text-xs font-medium sm:text-sm"
                htmlFor="default-price"
              >
                {t("calendar:pricing.pricePerLesson")}
              </label>
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <div className="bg-muted hover:bg-side-accent flex h-7 items-center self-start rounded-full px-2 sm:h-8 sm:self-auto sm:px-3">
                      <Select
                        disabled={isLoading || form.formState.isSubmitting}
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          form.clearErrors("amount");
                        }}
                      >
                        <FormControl>
                          <SelectTrigger className="h-7 min-w-[60px] border-0 bg-transparent p-0 px-1 text-xs shadow-none focus:ring-0 focus:ring-offset-0 sm:h-8 sm:min-w-[80px] sm:px-2 sm:text-sm">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent align={"center"} className={"z-50"}>
                          <SelectItem value={PriceCurrency.Eur}>
                            <div className="flex items-center gap-1 sm:gap-2">
                              <span className="text-xs">€</span>
                              <span className="text-xs sm:text-sm">EUR</span>
                            </div>
                          </SelectItem>
                          <SelectItem value={PriceCurrency.Gbp}>
                            <div className="flex items-center gap-1 sm:gap-2">
                              <span className="text-xs">£</span>
                              <span className="text-xs sm:text-sm">GBP</span>
                            </div>
                          </SelectItem>
                          <SelectItem value={PriceCurrency.Pln}>
                            <div className="flex items-center gap-1 sm:gap-2">
                              <span className="text-xs">zł</span>
                              <span className="text-xs sm:text-sm">PLN</span>
                            </div>
                          </SelectItem>
                          <SelectItem value={PriceCurrency.Usd}>
                            <div className="flex items-center gap-1 sm:gap-2">
                              <span className="text-xs">$</span>
                              <span className="text-xs sm:text-sm">USD</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <div className="bg-muted/20 group relative rounded-md backdrop-blur-sm">
                    <div className="absolute top-1/2 left-0 flex h-10 w-10 -translate-y-1/2 items-center justify-center bg-transparent transition-colors sm:h-12 sm:w-16">
                      <span className="text-accent-foreground text-lg font-semibold sm:text-xl">
                        {getCurrencySymbol(currency)}
                      </span>
                    </div>
                    <FormControl>
                      <Input
                        className={`hover:bg-muted/50 focus:bg-muted/10 h-10 appearance-none border-0 bg-transparent pl-10 text-base font-semibold transition-colors sm:h-14 sm:pl-16 lg:text-lg [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                          form.formState.errors.amount ? "border border-red-500" : ""
                        }`}
                        disabled={isLoading || form.formState.isSubmitting}
                        id="default-price"
                        min="0.01"
                        placeholder={t("calendar:pricing.yourLessonPrice")}
                        step="0.01"
                        type="number"
                        value={field.value === undefined ? "" : field.value}
                        onChange={(e) => {
                          const value =
                            e.target.value === "" ? undefined : Number.parseFloat(e.target.value);

                          if (value !== undefined && value > 9999) return;
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </div>
                  <FormMessage>
                    {form.formState.errors.amount && (
                      <div className="flex items-center gap-1 text-red-500">
                        <Icon height="1rem" icon="mdi:alert-circle-outline" width="1rem" />
                        <span>{form.formState.errors.amount.message}</span>
                      </div>
                    )}
                  </FormMessage>
                </FormItem>
              )}
            />

            {/* Price Tiers */}
            <AnimatePresence mode="wait">
              <motion.div
                key={`price-tiers-${currency}`}
                animate={{ opacity: 1 }}
                className="mt-8 space-y-4"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
              >
                <h4 className="text-muted-foreground text-sm font-medium">
                  {t("calendar:pricing.suggestedPrices")}
                </h4>

                {/* Mobile: 3 + 2 layout */}
                <div className="space-y-2 md:hidden">
                  <div className="grid grid-cols-3 gap-3">
                    {getPriceTiers(currency)
                      .slice(0, 3)
                      .map((price) => (
                        <button
                          key={`price-${price}`}
                          className="bg-muted hover:bg-side-accent flex h-9 items-center justify-center rounded-md text-xs font-medium transition-colors sm:h-10 sm:text-sm md:text-base"
                          type="button"
                          onClick={() => handlePriceTierClick(price)}
                        >
                          {getCurrencySymbol(currency)}
                          {price}
                        </button>
                      ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {getPriceTiers(currency)
                      .slice(3)
                      .map((price) => (
                        <button
                          key={`price-${price}`}
                          className="bg-muted hover:bg-side-accent flex h-9 items-center justify-center rounded-md text-xs font-medium transition-colors sm:h-10 sm:text-sm md:text-base"
                          type="button"
                          onClick={() => handlePriceTierClick(price)}
                        >
                          {getCurrencySymbol(currency)}
                          {price}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Desktop/tablet: single row when it fits */}
                <div className="hidden md:grid md:grid-cols-5 md:gap-3">
                  {getPriceTiers(currency).map((price) => (
                    <button
                      key={`price-${price}`}
                      className="bg-muted hover:bg-side-accent flex h-10 items-center justify-center rounded-md text-xs font-medium transition-colors sm:text-sm md:text-base"
                      type="button"
                      onClick={() => handlePriceTierClick(price)}
                    >
                      {getCurrencySymbol(currency)}
                      {price}
                    </button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </Form>
    </SettingsCard>
  );
};
